import React, { useEffect, useMemo, useState } from 'react';
import { legalAPI } from '../api';
import Markdown from '../components/Markdown';

/**
 * AdminLegalPages — calm editor for Impressum / Privacy / Terms.
 *
 * Layout: two columns on desktop (editor on the left, live preview on the
 * right) so admins can see the editorial result as they type. On narrow
 * widths the columns stack.
 *
 * Future-proof for translations: the backend already persists a
 * `translations` dict per page. When we add EN/PL/DE tabs we'll surface
 * them here without changing the storage shape.
 */
const SLUGS = ['impressum', 'privacy', 'terms'];
const LABEL = { impressum: 'Impressum', privacy: 'Privacy', terms: 'Terms' };

const AdminLegalPages = ({ showToast }) => {
  const [active, setActive] = useState('impressum');
  const [pages, setPages] = useState({}); // slug -> page
  const [draft, setDraft] = useState({}); // slug -> { eyebrow, title, intro, body }
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    legalAPI.adminList()
      .then((res) => {
        if (cancelled) return;
        const map = {};
        const initialDraft = {};
        (res.data?.pages || []).forEach((p) => {
          map[p.slug] = p;
          initialDraft[p.slug] = {
            eyebrow: p.eyebrow || '',
            title: p.title || '',
            intro: p.intro || '',
            body: p.body || '',
          };
        });
        setPages(map);
        setDraft(initialDraft);
      })
      .catch(() => showToast?.('Failed to load legal pages', 'error'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [showToast]);

  const current = pages[active];
  const currentDraft = draft[active] || { eyebrow: '', title: '', intro: '', body: '' };
  const dirty = useMemo(() => {
    if (!current) return false;
    return ['eyebrow', 'title', 'intro', 'body'].some(
      (k) => (currentDraft[k] || '') !== (current[k] || ''),
    );
  }, [current, currentDraft]);

  const setField = (k, v) => {
    setDraft((prev) => ({
      ...prev,
      [active]: { ...(prev[active] || {}), [k]: v },
    }));
  };

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const res = await legalAPI.adminUpdate(active, currentDraft);
      setPages((prev) => ({ ...prev, [active]: res.data }));
      showToast?.('Saved');
    } catch (err) {
      showToast?.(err.response?.data?.detail || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    if (!current) return;
    setDraft((prev) => ({
      ...prev,
      [active]: {
        eyebrow: current.eyebrow || '',
        title: current.title || '',
        intro: current.intro || '',
        body: current.body || '',
      },
    }));
  };

  const updatedHuman = (() => {
    if (!current?.updated_at) return null;
    try {
      const d = new Date(current.updated_at);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch (e) { return null; }
  })();

  if (loading) {
    return (
      <p style={{ fontStyle: 'italic', color: 'var(--kahla-text-light)' }}>
        Loading legal pages…
      </p>
    );
  }

  return (
    <div data-testid="admin-legal-pages" style={{ maxWidth: 1400 }}>
      {/* Page picker — calm uppercase row */}
      <div
        role="tablist"
        aria-label="Legal page"
        style={{
          display: 'flex', gap: 4,
          borderBottom: '1px solid var(--kahla-border)',
          marginBottom: 28,
        }}
      >
        {SLUGS.map((slug) => {
          const isActive = active === slug;
          return (
            <button
              key={slug}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-testid={`legal-tab-${slug}`}
              onClick={() => setActive(slug)}
              style={{
                background: 'none', border: 'none',
                padding: '10px 18px',
                fontFamily: 'inherit',
                fontSize: 11, letterSpacing: '0.2em',
                textTransform: 'uppercase',
                fontWeight: isActive ? 600 : 400,
                color: isActive ? 'var(--kahla-charcoal)' : 'var(--kahla-text-light)',
                borderBottom: isActive ? '1px solid var(--kahla-charcoal)' : '1px solid transparent',
                marginBottom: -1,
                cursor: 'pointer',
              }}
            >
              {LABEL[slug]}
            </button>
          );
        })}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 40,
        }}
        className="legal-editor-grid"
      >
        {/* ───── Editor column ───── */}
        <div>
          <p style={{
            fontFamily: 'Cormorant Garamond, Playfair Display, serif',
            fontStyle: 'italic',
            fontSize: 15, lineHeight: 1.7,
            color: 'var(--kahla-text-light)',
            margin: '0 0 24px',
          }}>
            Edit the page content. Markdown is supported for headings, links,
            **bold**, *italic*, and {`>`} blockquote callouts.
          </p>

          <Field label="Eyebrow (small label above the title)">
            <input
              type="text"
              value={currentDraft.eyebrow}
              onChange={(e) => setField('eyebrow', e.target.value)}
              data-testid="legal-field-eyebrow"
              style={inputStyle}
              maxLength={80}
            />
          </Field>

          <Field label="Title">
            <input
              type="text"
              value={currentDraft.title}
              onChange={(e) => setField('title', e.target.value)}
              data-testid="legal-field-title"
              style={inputStyle}
              maxLength={120}
            />
          </Field>

          <Field label="Intro (italic paragraph below title, optional)">
            <textarea
              value={currentDraft.intro}
              onChange={(e) => setField('intro', e.target.value)}
              data-testid="legal-field-intro"
              rows={3}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </Field>

          <Field label="Body (Markdown)">
            <textarea
              value={currentDraft.body}
              onChange={(e) => setField('body', e.target.value)}
              data-testid="legal-field-body"
              rows={22}
              style={{
                ...inputStyle,
                fontFamily: 'Menlo, Consolas, monospace',
                fontSize: 13,
                lineHeight: 1.6,
                resize: 'vertical',
              }}
            />
          </Field>

          <div style={{
            display: 'flex', gap: 12, alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 12, flexWrap: 'wrap',
          }}>
            <p style={{
              fontSize: 11, letterSpacing: '0.06em',
              color: 'var(--kahla-text-light)', margin: 0,
            }}>
              {updatedHuman ? `Last saved · ${updatedHuman}` : 'Not yet saved'}
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={handleRevert}
                disabled={!dirty || saving}
                data-testid="legal-revert"
                style={{
                  background: 'none', border: '1px solid var(--kahla-border)',
                  padding: '10px 18px',
                  fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.16em',
                  textTransform: 'uppercase', color: 'var(--kahla-text-light)',
                  cursor: dirty && !saving ? 'pointer' : 'not-allowed',
                  opacity: dirty && !saving ? 1 : 0.55,
                }}
              >Revert</button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!dirty || saving}
                data-testid="legal-save"
                style={{
                  background: 'var(--kahla-charcoal)', color: '#fff', border: 'none',
                  padding: '11px 22px',
                  fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  cursor: dirty && !saving ? 'pointer' : 'not-allowed',
                  opacity: dirty && !saving ? 1 : 0.55,
                }}
              >{saving ? 'Saving' : 'Save'}</button>
            </div>
          </div>
        </div>

        {/* ───── Preview column ───── */}
        <div>
          <p style={{
            fontSize: 10, letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--kahla-text-light)',
            margin: '0 0 14px',
          }}>Live preview</p>

          <div
            data-testid="legal-preview"
            className="legal-page"
            style={{
              background: '#faf8f5',
              border: '1px solid var(--kahla-border)',
              padding: '40px 36px 32px',
              minHeight: 480,
              maxHeight: 760,
              overflowY: 'auto',
            }}
          >
            <style>{`
              .legal-page h2 { font-family:'Playfair Display',serif; font-size:18px; font-weight:400; margin:32px 0 10px; color:var(--kahla-charcoal); }
              .legal-page p { font-size:14px; line-height:1.75; color:var(--kahla-text); margin:0 0 12px; }
              .legal-page .legal-divider { border:none; border-top:1px solid var(--kahla-border); margin:28px 0 0; }
              .legal-page .legal-callout { font-family:'Cormorant Garamond','Playfair Display',serif; font-style:italic; font-size:15px; line-height:1.7; color:var(--kahla-charcoal); padding:18px 22px; border-left:1px solid var(--kahla-border); margin:24px 0; background:rgba(255,247,235,0.45); }
              .legal-page a { color:var(--kahla-charcoal); text-decoration:underline; text-underline-offset:3px; }
            `}</style>
            {currentDraft.eyebrow && (
              <p style={{
                fontFamily: 'Playfair Display, serif',
                fontSize: 10, letterSpacing: '0.32em',
                textTransform: 'uppercase',
                color: 'var(--kahla-text-light)',
                margin: '0 0 12px', fontWeight: 400,
              }}>{currentDraft.eyebrow}</p>
            )}
            <h1 style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: 36, fontWeight: 400,
              letterSpacing: '-0.012em',
              color: 'var(--kahla-charcoal)',
              lineHeight: 1.12, margin: '0 0 20px',
            }}>{currentDraft.title || '—'}</h1>
            {currentDraft.intro && (
              <p style={{
                fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                fontStyle: 'italic', fontSize: 17, lineHeight: 1.7,
                color: 'var(--kahla-text-light)', margin: '0 0 12px',
              }}>{currentDraft.intro}</p>
            )}
            <Markdown source={currentDraft.body} />
          </div>
          <p style={{
            fontSize: 10, letterSpacing: '0.06em',
            color: 'var(--kahla-text-light)',
            marginTop: 10,
          }}>
            Preview renders the same Markdown subset as the public page
            (## headings, paragraphs, **bold**, *italic*, [links](),
            {' '} {`>`} callouts, --- dividers).
          </p>
        </div>
      </div>

      <style>{`
        @media (max-width: 980px) {
          .legal-editor-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 18 }}>
    <label style={{
      display: 'block',
      fontSize: 10, letterSpacing: '0.22em',
      textTransform: 'uppercase',
      color: 'var(--kahla-text-light)',
      marginBottom: 6,
    }}>{label}</label>
    {children}
  </div>
);

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontFamily: 'inherit',
  fontSize: 14,
  background: '#fff',
  border: '1px solid var(--kahla-border)',
  color: 'var(--kahla-charcoal)',
  outline: 'none',
};

export default AdminLegalPages;
